import type { TrackItem } from "./events";

/**
 * Conversores para `TrackItem`. Recebem só os campos que usam (formas
 * estruturais), para o módulo de medição não depender dos tipos do catálogo
 * e do carrinho — eles é que dependem dele.
 */

/** Produto de vitrine ou PDP (`ProductCardModel`). */
export function productToTrackItem(product: {
    productId: number;
    name: string;
    brand?: string;
    price: number;
}): TrackItem {
    return {
        item_id: String(product.productId),
        item_name: product.name,
        ...(product.brand ? { item_brand: product.brand } : {}),
        price: product.price,
        quantity: 1,
    };
}

/** Linha do carrinho da nest (`CartItemDto`). */
export function cartItemToTrackItem(
    item: { productId: number; name?: string; unitPrice?: number; quantity: number },
    quantity = item.quantity
): TrackItem {
    return {
        item_id: String(item.productId),
        item_name: item.name ?? `Produto ${item.productId}`,
        price: item.unitPrice ?? 0,
        quantity,
    };
}

/** Linha já montada pelo checkout (`CartLineItem`). */
export function lineToTrackItem(line: { id: string; name: string; unitPrice: number; quantity: number }): TrackItem {
    return { item_id: line.id, item_name: line.name, price: line.unitPrice, quantity: line.quantity };
}

type CartLike = { items?: ReadonlyArray<{ productId: number; name?: string; unitPrice?: number; quantity: number }> } | null;

/**
 * O que mudou entre dois carrinhos: o que entrou vira `add_to_cart`, o que
 * saiu vira `remove_from_cart`, com a quantidade da DIFERENÇA. Comparar o
 * resultado (e não o pedido) mede o que a API aceitou de fato — e cobre de
 * uma vez card, PDP, drawer e página do carrinho.
 */
export function diffCart(before: CartLike, after: CartLike): { added: TrackItem[]; removed: TrackItem[] } {
    const previous = new Map((before?.items ?? []).map((item) => [item.productId, item]));
    const next = new Map((after?.items ?? []).map((item) => [item.productId, item]));
    const added: TrackItem[] = [];
    const removed: TrackItem[] = [];

    for (const [productId, item] of next) {
        const delta = item.quantity - (previous.get(productId)?.quantity ?? 0);
        if (delta > 0) added.push(cartItemToTrackItem(item, delta));
        if (delta < 0) removed.push(cartItemToTrackItem(item, -delta));
    }
    for (const [productId, item] of previous) {
        if (!next.has(productId)) removed.push(cartItemToTrackItem(item));
    }

    return { added, removed };
}
